import { CreateObjective, NotificationAlert, Table } from '../../../components';
import { menteeObjectivescolumns } from './columns';
import { Button, DatePicker, DatePickerProps, Modal, Segmented } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { Paths } from '../../../routes';
import { useEffect, useRef, useState } from 'react';
import {
  menteeObjectiveProps,
  MenteesProps,
  MyObjectiveActions,
  USER_ROLES,
} from '../../../types';
import dayjs from 'dayjs';
import {
  getAllObjectives,
  updateObjectives,
} from '../../../services/objectiveServices';
import {
  Statuses,
  convertNameToPath,
  getUserProfilesFromStorage,
} from '../../../helper';
import { useAtomValue } from 'jotai';
import { userAtom } from '../../../store/atom';
import ViewUserTooltip from '../../../components/view-user-tooltip';
import UserProfile from '../../../components/profile/UserProfile';
import { getAnnualCycleById } from '../../../services/annualServices';
import { getQuarter } from '../../../services/reviewsServices';

const MenteeObjective = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<menteeObjectiveProps[]>([]);
  const [filtered, setFiltered] = useState<menteeObjectiveProps[]>([]);
  const [editID, setEditID] = useState<null | string>(null);
  const [modal, contextHolder] = Modal.useModal();
  const [loading, setLoading] = useState(true);
  const [callagain, setCallagain] = useState(true);
  const { selectedMentee } = useAtomValue(userAtom);
  const myRef = useRef<any>();
  const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<dayjs.Dayjs | null>(null);
  const { isQuarter, selectedCycleId } = useLocation().state || {};

  // Fetch annual cycle details to get the year
  useEffect(() => {
    const fetchCycleDetails = async () => {
      if (isQuarter) {
        if (!selectedCycleId) {
          myRef.current?.call('error', 'No cycle id found');
          setSelectedYear(dayjs());
          return;
        }
        try {
          const { data, error } = await getQuarter(selectedCycleId, 'mentor');
          if (!error && data?.year) {
            // Convert year string to dayjs object
            setSelectedYear(dayjs(`${data.year}-01-01`));
          } else {
            setSelectedYear(dayjs());
          }
        } catch (error) {
          console.error('Error fetching annual cycle details:', error);
          setSelectedYear(dayjs());
        }
      } else {
        if (!selectedCycleId) {
          myRef.current?.call('error', 'No cycle id found');
          setSelectedYear(dayjs());
          return;
        }
        try {
          const { data, error } = await getAnnualCycleById(
            selectedCycleId,
            'mentor'
          );
          if (!error && data?.year) {
            // Convert year string to dayjs object
            setSelectedYear(dayjs(`${data.year}-01-01`));
          } else {
            setSelectedYear(dayjs());
          }
        } catch (error) {
          console.error('Error fetching annual cycle details:', error);
          setSelectedYear(dayjs());
        }
      }
    };

    fetchCycleDetails();
  }, [selectedCycleId]);

  useEffect(() => {
    if (callagain && selectedYear) {
      (async () => {
        setOpen(false);
        setLoading(true);
        const { data, error } = await getAllObjectives(
          selectedMentee?.id || '',
          'mentor'
        );
        setCallagain(false);
        setLoading(false);
        if (error) return myRef.current?.call('error', error?.message);
        if (!error) {
          // remove delete objectives
          const filtered = data?.filter(
            (i) =>
              i.status.toLocaleLowerCase() !==
              Statuses.Delete.toLocaleLowerCase()
          );
          setData(filtered);
          // Apply default year filter
          applyYearFilter(filtered, selectedYear);
        }
      })();
    }
  }, [callagain, selectedYear]);

  useEffect(() => {
    const fetchProfilePic = async () => {
      if (!selectedMentee) return;
      try {
        const res = await getUserProfilesFromStorage();
        const profile = res.data.find((u: any) => u.id === selectedMentee.id);

        if (profile?.file instanceof Blob) {
          const objectUrl = URL.createObjectURL(profile.file);
          setProfilePicUrl(objectUrl);
        }
      } catch (error) {
        console.error('Error fetching profile picture:', error);
      }
    };

    fetchProfilePic();
  }, [selectedMentee]);

  // Apply year filter function
  const applyYearFilter = (
    objectivesData: menteeObjectiveProps[],
    date: dayjs.Dayjs | null
  ) => {
    if (date === null) {
      setFiltered(objectivesData);
      return;
    }

    if (objectivesData.length > 0) {
      const filter = objectivesData.filter(
        (i) =>
          dayjs(i.startDate).format('YYYY') === dayjs(date).format('YYYY') ||
          dayjs(i.endDate).format('YYYY') === dayjs(date).format('YYYY') ||
          (dayjs(date).isAfter(dayjs(i.startDate)) &&
            dayjs(date).isBefore(dayjs(i.endDate)))
      );
      setFiltered(filter);
    } else {
      setFiltered([]);
    }
  };

  // date change function
  const dateChange: DatePickerProps['onChange'] = (date) => {
    setLoading(true);
    setSelectedYear(date);
    applyYearFilter(data, date);
    setLoading(false);
  };

  const handleNav = (val: string) => {
    if (val === 'Quarterly Feedback') {
      navigate(
        `${Paths.QuarterlyReview}/${convertNameToPath(selectedMentee?.name || '')}`,
        {
          state: { from: 'mentor' },
        }
      );
    }
  };

  // delete objective
  const updateObjective = async (
    data: menteeObjectiveProps,
    action: MyObjectiveActions
  ) => {
    const { error } = await updateObjectives(
      {
        ...data,
        status: action === 'delete' ? Statuses.Delete : Statuses.NotStarted,
        ...(action !== 'delete' && {
          startDate: dayjs().format('YYYY-MM-DD'),
        }),
      },
      data.id
    );
    if (error) return myRef.current?.call('error', error?.message);
    setCallagain(true);
    myRef.current?.call('success');
  };

  // action button click
  const handleClick = (
    data: menteeObjectiveProps,
    action: MyObjectiveActions
  ) => {
    if (action === 'edit') {
      setOpen(true);
      setEditID(data?.id);
    }
    if (action === 'delete') {
      modal.confirm({
        title: 'Are you sure you want to delete this objective?',
        icon: <ExclamationCircleFilled />,
        okText: 'Yes',
        cancelText: 'No',
        onOk() {
          return updateObjective(data, action);
        },
      });
    }

    if (action === 'active') {
      updateObjective(data, action);
    }
  };

  const handleModal = (open: boolean) => {
    setOpen(open);
  };

  return (
    <div className="flex flex-col gap-y-[10px]">
      <div className="flex py-[10px] pl-[10px]">
        <div className="text-[20px] font-[500]">
          <ViewUserTooltip
            user={{
              ...(selectedMentee as MenteesProps),
              profilePic: profilePicUrl,
            }}
          />
        </div>
        <div className="ml-auto">
          <Segmented
            options={[
              'Objectives Dashboard',
              {
                label: 'Quarterly Feedback',
                value: 'Quarterly Feedback',
                disabled: false,
              },
            ]}
            defaultValue="Objectives Dashboard"
            onChange={handleNav}
          />
          <DatePicker
            onChange={dateChange}
            picker="year"
            className="ml-3 w-fit"
            placeholder="Year"
            value={selectedYear}
          />
        </div>
      </div>

      <div className="flex py-[10px] pl-[10px]">
        <div className="text-[20px] font-[500]">Objectives Dashboard</div>
        <div className="ml-auto">
          <Button
            type="primary"
            onClick={() => {
              setOpen(!open);
              setEditID(null);
            }}
          >
            Create Objective
          </Button>
        </div>
      </div>

      <Table
        columns={menteeObjectivescolumns(handleClick, USER_ROLES.MENTOR)}
        data={filtered}
        errorFetchingData={false}
        loading={loading}
        rowKey="id"
      />
      <CreateObjective
        open={open}
        setOpen={handleModal}
        editID={editID}
        setCallagain={setCallagain}
        from={USER_ROLES.MENTOR}
      />

      <UserProfile
        type="mentor"
        userId={selectedMentee?.id || ''}
        designation={selectedMentee?.designation || ''}
        tier={selectedMentee?.tier ?? ''}
      />

      <NotificationAlert ref={myRef} />
      {contextHolder}
    </div>
  );
};

export default MenteeObjective;
